import { get_user_id } from '@/router/index.js';

export const QUICK_EMOJIS = ['\u{1F44D}', '\u{2764}\u{FE0F}', '\u{263A}\u{FE0F}'];

const base_state = {
  is_open: false,
  is_available_override: true,
  launcher_hovered: false,
  launcher_pulsing: false,
  conversations: [],
  active_conversation_id: null,
  conversations_loading: false,
  conversations_loaded: false,
  hidden_conversation_ids: [],
  search_query: '',
  global_search_query: '',
  user_search: '',
  filter: 'all',
  active_section: 'recent',
  current_user_id: get_user_id() || null,
  current_user: null,
  toast: null,
};

const message_state = {
  messages: [],
  messages_loading: false,
  messages_offset: 0,
  messages_has_more: false,
  messages_total: 0,
  loading_more_messages: false,
  user_scrolled_up: false,
  new_messages_banner: false,
  draft: '',
  draft_error: '',
  sending: false,
  selected_file: null,
  selected_file_preview_url: null,
  reply_to: null,
  reacting_message_id: null,
  pinning_in_progress: false,
  pinned_messages: [],
  editing_message_id: null,
  edit_draft: '',
  saving_edit: false,
  pending_delete_message: null,
  pending_delete_scope: 'me',
  deleting_message: false,
  pending_conversation_action: null,
  conversation_action_loading: false,
};

const polling_state = {
  poll_timer: null,
  poll_frequency: 5000,
  _polling_active: false,
  _ready_for_poll: false,
  _messages_initialized: false,
  _is_adjusting_scroll: false,
  _portal_sync_in_flight: false,
};

const drawer_state = {
  active_context_menu: null,
  active_message_menu_id: null,
  message_menu_direction: [],
  search_overlay_open: false,
  conversation_details_open: false,
  new_chat_open: false,
  new_conversation_mode: 'direct',
  new_conversation_user_id: '',
  new_group_name: '',
  new_group_user_ids: [],
  new_conversation_message: '',
  starting_conversation: false,
  starting_group: false,
  add_member_open: false,
  group_members_updating: false,
  group_member_user_id: '',
  remove_member_target: null,
};

const mobile_state = {
  mobile_drag_start_y: 0,
  mobile_drag_delta: 0,
};

export const create_floating_chat_state = () => ({
  ...base_state,
  ...message_state,
  ...polling_state,
  ...drawer_state,
  ...mobile_state,
  quick_emojis: QUICK_EMOJIS,
  available_users: [],
  users_loading: false,
});

export const same_id = (left, right) => String(left) === String(right);

const id_key = value => String(value || '');

const metadata_readers = [
  message => message?.content,
  message => message?.is_pinned,
  message => message?.edited_at,
  message => message?.attachments,
  message => message?.reactions,
  message => message?.read_by,
  message => message?.read_count,
  message => message?.delivered_count,
];

const values_differ = (left, right) => JSON.stringify(left ?? null) !== JSON.stringify(right ?? null);

export const message_metadata_changed = (local, server_message) => metadata_readers
  .some(read => values_differ(read(local), read(server_message)));

export const build_silent_poll_update = (messages, latest_50) => {
  const existing_map = new Map(messages.map(message => [message.id, message]));
  const current_max_id = messages.length ? Math.max(...messages.map(message => message.id || 0)) : 0;
  const brand_new = latest_50.filter(message => !existing_map.has(message.id));
  const has_metadata_changes = latest_50.reduce((changed, server_message) => {
    const local = existing_map.get(server_message.id);
    if (!local || !message_metadata_changed(local, server_message)) return changed;
    existing_map.set(server_message.id, { ...local, ...server_message });
    return true;
  }, false);

  return ({
    brand_new,
    current_max_id,
    has_changes: Boolean(brand_new.length || has_metadata_changes),
    patched: messages.map(message => existing_map.get(message.id) || message),
  });
};

export const get_pending_conversation_action = action => {
  if (!['clear', 'delete', 'archive'].includes(action)) return null;
  return action === 'archive' ? 'delete' : action;
};

export const get_conversation_action_endpoint = action => (
  action === 'clear' ? 'clear_conversation_for_user' : 'delete_conversation_for_user'
);

export const build_group_member_ids = (group_user_ids, current_user_id) => [...new Set([
  ...group_user_ids.map(id => parseInt(id, 10)).filter(Boolean),
  parseInt(current_user_id, 10),
].filter(Boolean))];

export const is_group_conversation_record = conversation => {
  const type = String(
    conversation?.conversation_type
      || conversation?.type
      || conversation?.chat_type
      || conversation?.conversation_type_name
      || conversation?.group?.type
      || '',
  ).toLowerCase();
  const group_markers = [conversation?.is_group, conversation?.group_id, conversation?.group];
  const group_types = ['group', 'space', 'workspace'];
  return group_markers.some(Boolean) || group_types.some(item => type.includes(item));
};

export const get_name_initials = name => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length || name === 'Unknown') return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
};

const get_member_raw_user = raw_member => raw_member?.user || raw_member || {};

const get_member_id = (raw_member, raw_user) => (
  raw_user.id ||
  raw_member?.user_id ||
  raw_member?.id
);

const find_known_user = (available_users, member_id) => (
  available_users.find(user => same_id(user.id, member_id)) || {}
);

const get_conversation_role = (raw_member, user) => (
  raw_member?.role_name ||
  raw_member?.conversation_role_name ||
  user.conversation_role
);

const get_group_role = (raw_member, user) => (
  raw_member?.group_role ||
  user.group_role
);

const get_left_at = (raw_member, user) => (
  raw_member?.left_at ||
  user.left_at
);

export const normalize_chat_member = (
  raw_member,
  available_users,
  current_user_id,
  get_user_name
) => {
  const raw_user = get_member_raw_user(raw_member);
  const member_id = get_member_id(raw_member, raw_user);
  const known_user = find_known_user(available_users, member_id);
  const user = {
    ...known_user,
    ...raw_user };
  const id = user.id || member_id;
  const name = get_user_name(user);

  return ({
    ...user,
    id,
    name,
    initials: get_name_initials(name),
    conversation_role: get_conversation_role(raw_member, user),
    group_role: get_group_role(raw_member, user),
    left_at: get_left_at(raw_member, user),
    is_me: same_id(id, current_user_id),
  });
};

export const unique_visible_members = members => {
  const seen = new Set();
  return members.filter(member => {
    const key = id_key(member.id || member.email || member.name);
    if (!key || seen.has(key) || member.left_at) return false;
    seen.add(key);
    return true;
  });
};
